"use client"

import { useEffect, useMemo, useState } from "react"
import { SEARCH_PARAMS_PAGE_VIEW_OPTIONS, SearchArticleViewOptions } from "@/constants/ui"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"

import { UserData } from "@/lib/intershop/interfaces/user/user"
import type { MarketChannel } from "@/lib/market/market-channel"
import { CategoryDiscount, ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import ProductCard from "@/components/product/ProductCard"
import ProductCardLandscape from "@/components/product/ProductCardLandscape"

interface StorefrontContextResponse {
  isB2B: boolean
  oci: boolean
  userData?: UserData
}

interface ProductPricesResponse {
  prices?: ProductPriceContainerCtnrROV1
  isB2B?: boolean
  isAnonymous?: boolean
  customerCategoryDiscount?: number
  categoryDiscount?: CategoryDiscount
}

const storefrontContextPromiseCache = new Map<string, Promise<StorefrontContextResponse | undefined>>()

function loadStorefrontContext(sbLocale?: string) {
  const cacheKey = sbLocale ?? ""
  let storefrontContextPromise = storefrontContextPromiseCache.get(cacheKey)

  if (!storefrontContextPromise) {
    const url = sbLocale
      ? `/api/storefront/context?sbLocale=${encodeURIComponent(sbLocale)}`
      : "/api/storefront/context"
    storefrontContextPromise = fetch(url, {
    cache: "no-store",
  }).then((response) => (response.ok ? response.json() : undefined))
    storefrontContextPromiseCache.set(cacheKey, storefrontContextPromise)
  }

  return storefrontContextPromise
}

export default function ProductCardWrapper(props: {
  productData: ReducedProductData
  effectivePrices: ProductPriceContainerCtnrROV1 | undefined
  view: SearchArticleViewOptions | string
  cardVariant: string
  isB2B: boolean
  showEcoTax: boolean
  debugMode: boolean | undefined
  hideAddToBasket: boolean
  priceRequestLink: string
  isoLocale: string
  sbLocale?: string
  channel?: MarketChannel
  trackingList: string | undefined
  position: number | undefined
  mainProductId: string | undefined
  isRecommendation: boolean | undefined
  inStoryBlock: boolean | undefined
  ffSearchParamsEncrypted?: string
  categoryPath?: string[]
  searchTerm?: string
  campaignId?: string
}) {
  const { isB2BCustomer, setIsB2BCustomer, setUserData, setIsOCI, userData } = useUbStorefrontState()
  const [effectivePrices, setEffectivePrices] = useState<ProductPriceContainerCtnrROV1 | undefined>(
    props.effectivePrices
  )
  const [customerCategoryDiscount, setCustomerCategoryDiscount] = useState<number | undefined>()
  const [categoryDiscount, setCategoryDiscount] = useState<CategoryDiscount | undefined>(props.productData.categoryDiscount)
  const [priceRequestCompleted, setPriceRequestCompleted] = useState(false)
  const productId = props.productData?.id

  useEffect(() => {
    setEffectivePrices(props.effectivePrices)
    setCustomerCategoryDiscount(undefined)
    setCategoryDiscount(props.productData.categoryDiscount)
    setPriceRequestCompleted(false)
  }, [productId, props.effectivePrices, props.productData.categoryDiscount])

  useEffect(() => {
    let cancelled = false

    loadStorefrontContext(props.sbLocale)
      .then((context) => {
        if (!cancelled && context) {
          setIsB2BCustomer(context.isB2B)
          setUserData(context.userData)
          setIsOCI(context.oci)
        }
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [props.sbLocale, setIsB2BCustomer, setIsOCI, setUserData])

  useEffect(() => {
    if (priceRequestCompleted || !productId) {
      return
    }

    let cancelled = false

    const searchParams = new URLSearchParams()
    if (props.sbLocale) {
      searchParams.set("sbLocale", props.sbLocale)
    }
    if (props.channel) {
      searchParams.set("channel", props.channel)
    }
    const queryString = searchParams.toString()
    const url = `/api/storefront/product-prices/${encodeURIComponent(productId)}${queryString ? `?${queryString}` : ""}`

    fetch(url, {
      cache: "no-store",
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((data: ProductPricesResponse | undefined) => {
        if (!cancelled) {
          setEffectivePrices(props.effectivePrices ?? data?.prices)
          setCustomerCategoryDiscount(data?.customerCategoryDiscount)
          setCategoryDiscount(data?.categoryDiscount ?? props.productData.categoryDiscount)
          if (data?.isB2B !== undefined) {
            setIsB2BCustomer(data.isB2B)
          }
          setPriceRequestCompleted(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPriceRequestCompleted(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    priceRequestCompleted,
    productId,
    props.channel,
    props.effectivePrices,
    props.productData.categoryDiscount,
    props.sbLocale,
    setIsB2BCustomer,
  ])

  const effectiveIsB2B = useMemo(() => isB2BCustomer ?? props.isB2B, [isB2BCustomer, props.isB2B])
  const priceLoading = !!productId && !effectivePrices && !priceRequestCompleted
  const productData = useMemo(
    () =>
      categoryDiscount && categoryDiscount !== props.productData.categoryDiscount
        ? { ...props.productData, categoryDiscount }
        : props.productData,
    [categoryDiscount, props.productData]
  )

  return (
    <>
      {(props.cardVariant === "landscape" && (
        <div
          className="
            relative z-0 flex w-full flex-col overflow-hidden bg-white transition duration-200 ease-in-out
            hover:z-50 hover:scale-105 hover:rounded-sm
          "
        >
          <ProductCardLandscape
            isoLocale={props.isoLocale}
            productData={productData}
            prices={effectivePrices}
            priceLoading={priceLoading}
            showDescription={true}
            isB2BCustomer={effectiveIsB2B}
            generalDiscount={userData?.generalDiscount}
            customerCategoryDiscount={customerCategoryDiscount}
            inStoryBlok={props.inStoryBlock ?? false}
            ffSearchParamsEncrypted={props.ffSearchParamsEncrypted}
            categoryPath={props.categoryPath}
            position={props.position}
            trackingList={props.trackingList}
            searchTerm={props.searchTerm}
            campaignId={props.campaignId}
            mainProductId={props.mainProductId}
            isRecommendation={props.isRecommendation}
          />
        </div>
      )) || (
        <div
          className={cn(
            `
              relative flex size-full flex-col overflow-hidden rounded-lg border border-gray-300 bg-white transition
              duration-200 ease-in-out
              3xl:max-w-100
            `,
            {
              "3xl:max-w-full": props.view === SEARCH_PARAMS_PAGE_VIEW_OPTIONS[1],
            }
          )}
        >
          <ProductCard
            isoLocale={props.isoLocale}
            productData={productData}
            prices={effectivePrices}
            priceLoading={priceLoading}
            showDescription={true}
            showEcoTaxFlag={props.showEcoTax}
            debugMode={props.debugMode}
            hideAddToBasket={props.hideAddToBasket}
            isB2BCustomer={effectiveIsB2B}
            generalDiscount={userData?.generalDiscount}
            customerCategoryDiscount={customerCategoryDiscount}
            trackingList={props.trackingList}
            position={props.position}
            priceRequestLink={props.priceRequestLink}
            view={props.view}
            mainProductId={props.mainProductId}
            isRecommendation={props.isRecommendation}
            ffSearchParamsEncrypted={props.ffSearchParamsEncrypted}
            categoryPath={props.categoryPath}
            searchTerm={props.searchTerm}
            campaignId={props.campaignId}
            compact={false}
          />
        </div>
      )}
    </>
  )
}
